import React, { useEffect, useState } from 'react';
import { CategoriaCircularizacao, IIndicadoresCircularizacao } from '../interfaces/ICircularizacao';
import { CircularizacaoService } from '../services/CircularizacaoService';
import { obterLabelCategoria } from '../utils/categoriaConteudo';

interface Props {
  // Escopo real é por esteira/período (mesma convenção do resto do módulo de Auditoria), não por
  // empresa — uma empresa pode ter um processo de circularização por período de auditoria.
  periodo: string;
  onGerenciar: () => void;
  // Seção 2 — "permitir navegação direta para solucioná-las": leva direto para a aba de
  // Planejamento da categoria específica que está com prazo vencido.
  onResolverPendencia: (categoria: CategoriaCircularizacao) => void;
}

type EstadoEtapa = 'NAO_INICIADO' | 'EM_ANDAMENTO' | 'PENDENTE' | 'CONCLUIDO' | 'NAO_SE_APLICA';

// Estados da seção 2: Não iniciado / Em andamento / Pendente / Concluído — preparação / Não se
// aplica. "Pendente" é reservado para quando há prazo de resposta já vencido (seção 7.1 —
// "identificar datas vencidas") — mais urgente do que simplesmente estar em andamento.
const calcularEstado = (ind: IIndicadoresCircularizacao): EstadoEtapa => {
  if (ind.empresasAtivas === 0) return ind.empresasSugeridas === 0 ? 'NAO_SE_APLICA' : 'NAO_INICIADO';
  if (ind.prazosVencidos > 0) return 'PENDENTE';
  if (ind.cartasPreparadas === 0) return 'NAO_INICIADO';
  if (ind.cartasPendentes > 0) return 'EM_ANDAMENTO';
  return 'CONCLUIDO';
};

const LABEL_ESTADO: Record<EstadoEtapa, string> = {
  NAO_INICIADO: 'NÃO INICIADO',
  EM_ANDAMENTO: 'EM ANDAMENTO',
  PENDENTE: 'PENDENTE',
  CONCLUIDO: 'CONCLUÍDO — PREPARAÇÃO',
  NAO_SE_APLICA: 'NÃO SE APLICA',
};

const COR_ESTADO: Record<EstadoEtapa, string> = {
  NAO_INICIADO: '#8a92a3',
  EM_ANDAMENTO: '#8a6d1d',
  PENDENTE: '#9b1c1c',
  CONCLUIDO: '#2e7d32',
  NAO_SE_APLICA: '#8a92a3',
};

// Prompt Mestre V3.0, seção 2/11 — "participar da barra de progressão já existente no módulo de
// Auditoria" (não criar outra barra principal). Participa agora como card de resumo no topo da
// área principal (ao lado da barra de Revisão do Laudo), mesmo padrão visual do dashboard.
const AuditoriaCircularizacaoBarraProgressao: React.FC<Props> = ({ periodo, onGerenciar, onResolverPendencia }) => {
  const [indicadores, setIndicadores] = useState<IIndicadoresCircularizacao | undefined>();
  const [categoriasVencidas, setCategoriasVencidas] = useState<{ categoria: CategoriaCircularizacao; quantidade: number }[]>([]);

  useEffect(() => {
    if (!periodo) {
      setIndicadores(undefined);
      setCategoriasVencidas([]);
      return;
    }
    let ativo = true;
    const esteiraId = Number(periodo);
    Promise.all([
      CircularizacaoService.obterIndicadores(esteiraId),
      CircularizacaoService.listarCategoriasComPrazoVencido(esteiraId),
    ]).then(([ind, vencidas]) => {
      if (ativo) {
        setIndicadores(ind);
        setCategoriasVencidas(vencidas);
      }
    });
    return () => {
      ativo = false;
    };
  }, [periodo]);

  if (!indicadores) return null;

  const estado = calcularEstado(indicadores);
  const percentual = indicadores.percentualPreparacao ?? 0;
  const naoSeAplica = estado === 'NAO_SE_APLICA';

  return (
    <div className='auditoria-banner-card'>
      <div className='auditoria-banner-info'>
        <h5 className='auditoria-banner-title'>Cartas de Circularização</h5>
        <p className='auditoria-banner-subtitle'>Acompanhe o andamento das cartas desta auditoria.</p>
      </div>

      {naoSeAplica ? (
        <p className='auditoria-banner-subtitle mb-0' style={{ color: '#8a92a3' }}>
          Nenhum destinatário sugerido para esta empresa/período.
        </p>
      ) : (
        <>
          <div className='auditoria-banner-metric'>
            <strong>
              {indicadores.cartasPreparadas} de {indicadores.empresasAtivas}
            </strong>
            <span>cartas concluídas</span>
          </div>

          {indicadores.cartasPendentes > 0 && (
            <span className='auditoria-banner-badge'>
              <i className='bi bi-clock-fill me-1' />
              {indicadores.cartasPendentes} pendente{indicadores.cartasPendentes === 1 ? '' : 's'}
            </span>
          )}

          <div className='auditoria-banner-progress'>
            <div className='progress' style={{ height: 8 }}>
              <div
                className='progress-bar'
                role='progressbar'
                style={{ width: `${percentual}%` }}
                aria-valuenow={percentual}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            <span className='auditoria-banner-progress-pct'>{percentual}%</span>
          </div>
        </>
      )}

      <button type='button' className='btn btn-primary auditoria-banner-btn' onClick={onGerenciar}>
        Gerenciar Circularizações
        <i className='bi bi-arrow-right-short' />
      </button>

      {!naoSeAplica && (
        <div className='auditoria-banner-detail'>
          <span style={{ color: COR_ESTADO[estado], fontWeight: 700, fontSize: 11.5 }}>{LABEL_ESTADO[estado]}</span>

          {categoriasVencidas.length > 0 && (
            <div className='mt-1'>
              <div className='text-danger' style={{ fontSize: 11.5, fontWeight: 700 }}>
                <i className='bi bi-exclamation-triangle-fill me-1' />
                Prazos vencidos — resolver agora:
              </div>
              {categoriasVencidas.map(({ categoria, quantidade }) => (
                <button
                  key={categoria}
                  type='button'
                  className='btn btn-link p-0 d-block text-danger'
                  style={{ fontSize: 12 }}
                  onClick={() => onResolverPendencia(categoria)}
                >
                  {obterLabelCategoria(categoria)} ({quantidade}) <i className='bi bi-arrow-right-short' />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditoriaCircularizacaoBarraProgressao;
